import { ErrorMessage, Field, FieldAttributes, Form, Formik, FormikHelpers, FormikProps, useField, useFormikContext } from "formik";
import { FC, ReactNode } from "react";
import * as Yup from "yup";
import CustomSelect from "./Select";

export type FormField = {
  id: string,
  label?: string | ReactNode,
  name: string,
  type: string,
  className?: string,
  required?: boolean,
  placeholder?: string,
  validation?: any,
  labelClass?: string,
  disabled?: boolean,
  options?: { label: string, value: any }[],
  icon?: ReactNode,
  description?: ReactNode | string,
  onChange?: CallableFunction,
  customElement?: ReactNode,
  [key: string]: any
}

type Props<T = any> = {
  id: string,
  formFields: FormField[],
  initialValues?: any,
  onSubmit: (values: T, formikHelpers: FormikHelpers<T>) => void | Promise<any>,
  onChange?: React.FormEventHandler<HTMLFormElement>,
  className?: string,
  submitText?: string | JSX.Element,
  hideSubmitButton?: boolean,
  showResetButton?: boolean,
  resetButton?: boolean,
  validationSchema?: Yup.ObjectSchema<{ [x: string]: any }, Yup.AnyObject, { [x: string]: any }, "">,
}

function customValidationSchema(formFields: FormField[]) {
  let validations: any = {}
  formFields.forEach((value: FormField) => {
    validations[value.name] = value.validation
  });

  return Yup.object().shape(validations);
}

const SelectField: FC<
  FieldAttributes<FormField> & {
    form: FormikProps<HTMLFormElement>, field: FormField
  }> = ({ field: customField, onChange, ...props }) => {
    const {
      setFieldValue,
      setFieldTouched
    } = useFormikContext();
    const [field, meta, helpers] = useField(customField.name)

    const handleChange = async (event: any) => {
      await setFieldValue(field.name, event.value);
      setFieldTouched(field.name)
      // console.log(field.value, field.value !== "")
      if (onChange)
        onChange(event)
    }

    return <CustomSelect
      {...field}
      {...meta}
      name={field.name}
      className="form-select-sm"
      options={props.options || []}
      onChange={handleChange}
      placeholder={props.placeholder}
      disabled={props.disabled}
      value={props.options?.filter((option: any) => option.value !== "add_player").find((option: any) => option.value === field.value) || ""}
      onBlur={() => helpers.setTouched(true)}
    />
  }

export default function CustomForm(props: Props) {
  const { id, formFields = [], onSubmit, onChange, className, hideSubmitButton = false, showResetButton = false, submitText, validationSchema } = props;
  let { initialValues = {} } = props;

  formFields.forEach((field: FormField, _index: number) => {
    if (!initialValues.hasOwnProperty(field.name)) {
      initialValues[field.name] = ''
    }
  })

  return (
    <Formik
      initialValues={initialValues ? initialValues : []}
      onSubmit={onSubmit}
      validationSchema={validationSchema ? validationSchema : customValidationSchema(formFields)}
      validateOnChange={false}
      enableReinitialize={true}
    // validateOnBlur={false}
    >
      {(formProps: FormikProps<FormField>) => <Form
        id={id}
        className={"form" + (className ? ' ' + className : '')}
        onChange={onChange}
      >
        {formFields.length ? formFields.map((field: FormField, index: number) => {
          let fieldInput: ReactNode = <>
            {field.icon ? field.icon : <></>}
            <Field
              id={field.id}
              name={field.name}
              type={field.type}
              className={field.className}
              placeholder={field.placeholder}
              options={field.options}
              component={field.type === "select" ? SelectField : (field.type === "textarea" ? "textarea" : undefined)}
              disabled={field.disabled}
              onBlur={formProps.getFieldProps(field.name).onBlur}
              onChange={field.type === "select" ? onChange : formProps.getFieldProps(field.name).onChange}
            />
          </>
  
          if (
            field.type === "custom" &&
            typeof field.customElement !== "undefined"
          ) {
            fieldInput = field.customElement
          }

          if (field.type === "radio") {
            fieldInput = <>
            <Field id={field.id} name={field.name} type="hidden" value={formProps.values[field.name]} />
              <input
                id={field.id}
                name={field.name}
                type="radio"
                onChange={(event: React.ChangeEvent<any>) => {
                  console.log('event:', event)
                  formProps.setFieldValue(field.name, event.target.value)
                }}
              />
            </>
          }

          return <div key={index} className="form-row">
            <div className="form-label">
              {field.label && (
                <label htmlFor={field.id} className={field.labelClass}>
                  {typeof field.label === 'string'
                    ? field.label + ':' // + (field.required ? ' *' : '')
                    : field.label
                  }
                </label>
              )}
            </div>
            <div className="form-input pb-3">
              {fieldInput}
              <span className="form-error">
                <ErrorMessage
                  name={field.name}
                  component="div"
                  className="alert alert-danger"
                />
              </span>
              {field.description ? <div className="form-description pt-3">
                {field.description}
              </div> : <></>}
            </div>
          </div>
        }): <></>}
        {!hideSubmitButton && <button type="submit" className="btn btn-primary">{submitText ? submitText : 'Invia'}</button>}
        {showResetButton && <button type="reset" className="btn btn-warning ms-1">Reset</button>}
      </Form>}
    </Formik>
  );
}
